const MAIL_PASSWORDS_KEY = 'mailPasswordsByEmail';
const LEGACY_MAIL_PASSWORD_KEY = 'mailPassword';

/** In-memory cache; source of truth until logout is localStorage. */
const passwordByEmail = new Map();

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const getStoredUserEmail = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return normalizeEmail(user?.email);
  } catch {
    return '';
  }
};

const readPasswordMap = () => {
  try {
    const raw = localStorage.getItem(MAIL_PASSWORDS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return map && typeof map === 'object' ? map : {};
  } catch {
    return {};
  }
};

const writePasswordMap = (map) => {
  localStorage.setItem(MAIL_PASSWORDS_KEY, JSON.stringify(map));
};

const hydrateFromStorage = () => {
  passwordByEmail.clear();
  Object.entries(readPasswordMap()).forEach(([email, password]) => {
    const key = normalizeEmail(email);
    if (key && password) {
      passwordByEmail.set(key, password);
    }
  });
};

/**
 * Migrate legacy single-key password into the per-email map.
 */
const migrateLegacyPassword = () => {
  try {
    const legacy =
      localStorage.getItem(LEGACY_MAIL_PASSWORD_KEY) ||
      sessionStorage.getItem(LEGACY_MAIL_PASSWORD_KEY);
    if (!legacy) return;

    const email = getStoredUserEmail();
    const map = readPasswordMap();
    if (email && !map[email]) {
      map[email] = legacy;
      writePasswordMap(map);
    }

    localStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
    sessionStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
  } catch {
    // ignore
  }
};

// Drop temporary sessionStorage copy from the earlier session-only approach.
try {
  sessionStorage.removeItem(MAIL_PASSWORDS_KEY);
  sessionStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
} catch {
  // ignore
}

migrateLegacyPassword();
hydrateFromStorage();

/**
 * Mailbox password for IMAP/SMTP (x-mail-password).
 * Kept until explicit logout so refresh does not send the user back to login.
 */
export const getMailPassword = (email) => {
  const key = normalizeEmail(email) || getStoredUserEmail();
  if (!key) return '';

  if (passwordByEmail.has(key)) {
    return passwordByEmail.get(key);
  }

  const stored = readPasswordMap()[key];
  if (stored) {
    passwordByEmail.set(key, stored);
    return stored;
  }

  return '';
};

export const setMailPassword = (password, email) => {
  if (!password) return;
  const key = normalizeEmail(email) || getStoredUserEmail();
  if (!key) return;

  passwordByEmail.set(key, password);

  const map = readPasswordMap();
  map[key] = password;
  writePasswordMap(map);

  localStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
  sessionStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
  sessionStorage.removeItem(MAIL_PASSWORDS_KEY);
};

export const clearMailPassword = (email) => {
  if (email) {
    const key = normalizeEmail(email);
    passwordByEmail.delete(key);
    const map = readPasswordMap();
    delete map[key];
    writePasswordMap(map);
    return;
  }

  passwordByEmail.clear();
  localStorage.removeItem(MAIL_PASSWORDS_KEY);
  localStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
  sessionStorage.removeItem(MAIL_PASSWORDS_KEY);
  sessionStorage.removeItem(LEGACY_MAIL_PASSWORD_KEY);
};

export const clearAllMailPasswords = () => clearMailPassword();

export const hasMailPassword = (email) => Boolean(getMailPassword(email));

/** @deprecated No-op kept for older callers; passwords stay until logout. */
export const purgeStoredMailPasswords = () => {
  migrateLegacyPassword();
  hydrateFromStorage();
};
